import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useInViewAnimation } from '@/hooks/useInViewAnimation';
import { VideoExampleModal, VideoExampleWithScenes } from './VideoExampleModal';
import { useVideoExamples, VideoExample } from '@/hooks/useVideoExamples';

// Helper to detect if URL is a direct video file
const isDirectVideo = (url: string | null) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    return /\.(mp4|webm|mov)$/i.test(urlObj.pathname);
  } catch {
    return false;
  }
};

// Modern Carousel Card Component - Large, clean design
const CarouselCard = ({
  example,
  onPlay,
  isPaused = false,
}: {
  example: VideoExample;
  onPlay: () => void;
  isPaused?: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Pause/Resume based on parent isPaused state
  useEffect(() => {
    if (!videoRef.current) return;
    
    if (isPaused) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
  }, [isPaused]);

  // Intersection Observer - pause videos not in viewport
  useEffect(() => {
    if (!videoRef.current || !cardRef.current || isPaused) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!videoRef.current) return;
        if (entry.isIntersecting) {
          videoRef.current.play().catch(() => {});
        } else {
          videoRef.current.pause();
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [isPaused]);

  return (
    <div
      ref={cardRef}
      className="relative aspect-video rounded-xl overflow-hidden cursor-pointer group bg-muted"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onPlay}
    >
      {/* Video Background */}
      {example.video_url && isDirectVideo(example.video_url) ? (
        <video
          ref={videoRef}
          src={example.video_url}
          className="absolute inset-0 w-full h-full object-cover"
          muted
          loop
          playsInline
          preload="metadata"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20" />
      )}

      {/* Hover Overlay */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-8 h-8 text-white fill-white" />
          </div>
        </div>
      </div>

      {/* Title Badge */}
      <div className="absolute bottom-4 left-4 right-4">
        <div className="bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2">
          <h3 className="text-white font-medium text-sm">{example.title}</h3>
          {example.duration && (
            <p className="text-white/70 text-xs">{example.duration}</p>
          )}
        </div>
      </div>
    </div>
  );
};

interface VideoExamplesSectionProps {
  isPaused?: boolean;
}

export function VideoExamplesSection({ isPaused = false }: VideoExamplesSectionProps) {
  const { ref, isInView } = useInViewAnimation({ threshold: 0.2 });
  const { examples: VIDEO_EXAMPLES, loading, error } = useVideoExamples();
  const [selectedVideo, setSelectedVideo] = useState<VideoExample | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const autoScrollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Group videos into pairs for carousel slides
  const slides = [];
  for (let i = 0; i < VIDEO_EXAMPLES.length; i += 2) {
    slides.push(VIDEO_EXAMPLES.slice(i, i + 2));
  }

  const totalSlides = slides.length;

  // Auto-scroll when in view
  useEffect(() => {
    if (isInView && !isPaused && !selectedVideo && totalSlides > 0) {
      autoScrollRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, 5000);
    } else {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    }

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [isInView, isPaused, selectedVideo, totalSlides]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    // Reset auto-scroll timer
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
      }, 5000);
    }
  }, [totalSlides]);

  const handlePrev = useCallback(() => {
    goToSlide(currentSlide === 0 ? totalSlides - 1 : currentSlide - 1);
  }, [currentSlide, totalSlides, goToSlide]);

  const handleNext = useCallback(() => {
    goToSlide((currentSlide + 1) % totalSlides);
  }, [currentSlide, totalSlides, goToSlide]);

  const handlePlayVideo = (example: VideoExample) => {
    setSelectedVideo(example);
  };

  const handleNavigate = useCallback((direction: 'prev' | 'next') => {
    if (!selectedVideo) return;
    const currentIndex = VIDEO_EXAMPLES.findIndex(v => v.id === selectedVideo.id);
    if (direction === 'prev') {
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : VIDEO_EXAMPLES.length - 1;
      setSelectedVideo(VIDEO_EXAMPLES[prevIndex]);
    } else {
      const nextIndex = currentIndex < VIDEO_EXAMPLES.length - 1 ? currentIndex + 1 : 0;
      setSelectedVideo(VIDEO_EXAMPLES[nextIndex]);
    }
  }, [selectedVideo, VIDEO_EXAMPLES]);

  // Don't render section if no videos
  if (!loading && VIDEO_EXAMPLES.length === 0) {
    return null;
  }

  return (
    <section ref={ref} className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Real-World Scenarios
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            See what our AI Story Director can create for you
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Carousel */}
            <div className="relative">
              {/* Navigation Buttons */}
              {totalSlides > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 hidden md:flex bg-background/80 backdrop-blur-sm"
                    onClick={handlePrev}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 hidden md:flex bg-background/80 backdrop-blur-sm"
                    onClick={handleNext}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </>
              )}

              {/* Slides Container */}
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {slides.map((slideVideos, slideIndex) => (
                    <div
                      key={slideIndex}
                      className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-2 gap-6 px-2"
                    >
                      {slideVideos.map((video) => (
                        <CarouselCard
                          key={video.id}
                          example={video}
                          onPlay={() => handlePlayVideo(video)}
                          isPaused={isPaused || !!selectedVideo}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Dots Indicator */}
              {totalSlides > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all ${
                        currentSlide === index
                          ? 'bg-primary w-6'
                          : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                      }`}
                      onClick={() => goToSlide(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <VideoExampleModal
          example={selectedVideo as VideoExampleWithScenes}
          isOpen={true}
          onClose={() => setSelectedVideo(null)}
          hasNavigation={VIDEO_EXAMPLES.length > 1}
          onNavigate={handleNavigate}
        />
      )}
    </section>
  );
}
